import { useApolloClient, useMutation } from '@apollo/client';
import { CREATE_WAGE_HISTORY, UPDATE_WAGE_HISTORY } from 'apollo/mutations/wage-history-mutations';
import { QUERY_WAGE_HISTORY } from 'apollo/queries/wage-history-queries';
import isNil from 'lodash/isNil';
import { DateTime } from 'luxon';
import WageRate from 'types/enum/WageRate';
import IWageHistory, { IWageHistoryCreateInput, IWageHistoryUpdateInput } from 'types/WageHistory';

export default function useWageHistory() {
  const [createWageHistoryMutation] = useMutation<{ createWageHistory: IWageHistory }>(CREATE_WAGE_HISTORY);
  const [updateWageHistoryMutation] = useMutation<{ updateWageHistory: IWageHistory }>(UPDATE_WAGE_HISTORY);
  const client = useApolloClient();

  async function getWageHistoriesForMember(memberId: string) {
    const results = await client.query<{ wageHistories: IWageHistory[] }>({
      query: QUERY_WAGE_HISTORY,
      variables: { memberId },
      fetchPolicy: 'network-only',
    });

    return results.data.wageHistories;
  }

  async function createWageHistory(
    memberId: string,
    wageRate: WageRate,
    wage: number,
    overburden: number,
    changeDate: string,
    workWeek?: number | null
  ) {
    const newChangeDate = DateTime.fromISO(changeDate)
      .startOf('day')
      .toISO({ suppressMilliseconds: true, includeOffset: false });

    const item: IWageHistoryCreateInput = {
      memberId,
      wageRate,
      wage,
      overburden,
      changeDate: newChangeDate,
      workWeek,
      createdOn: DateTime.utc().toISO().toString(),
    };

    return new Promise<IWageHistory>((resolve, reject) => {
      return createWageHistoryMutation({ variables: { wageHistory: item } }).then((results) => {
        if (results.data) {
          resolve(results.data.createWageHistory);
        } else {
          reject();
        }
      });
    });
  }

  async function updateWageHistory(
    wageHistoryId: string,
    wage?: number | null,
    wageRate?: WageRate | null,
    workWeek?: number | null,
    overburden?: number | null,
    changeDate?: string | null
  ) {
    let newChangeDate: string | null = null;
    if (!isNil(changeDate)) {
      newChangeDate = DateTime.fromISO(changeDate)
        .startOf('day')
        .toISO({ suppressMilliseconds: true, includeOffset: false });
    }

    const item: IWageHistoryUpdateInput = {
      id: wageHistoryId,
      wage,
      wageRate,
      workWeek,
      overburden,
      changeDate: newChangeDate ? newChangeDate : undefined,
    };

    return new Promise<IWageHistory>((resolve, reject) => {
      return updateWageHistoryMutation({ variables: { wageHistory: item } }).then((results) => {
        if (results.data) {
          resolve(results.data.updateWageHistory);
        } else {
          reject();
        }
      });
    });
  }

  function deleteWageHistory(wageHistory: IWageHistory) {
    const item: IWageHistoryUpdateInput = {
      id: wageHistory.id,
      deletedOn: DateTime.utc().toISO(),
    };
    return updateWageHistoryMutation({ variables: { wageHistory: item } });
  }

  return { getWageHistoriesForMember, createWageHistory, updateWageHistory, deleteWageHistory };
}
